/**
 * The data the demo backend starts from: three servers, six instances, and between them every
 * state the interface has to be able to show. Times are relative to `now`, so the demo always
 * looks freshly recorded.
 *
 * | Instance | Server | What it shows |
 * |---|---|---|
 * | openclaw-micio | eu-1 | running, a draft revision waiting to be applied |
 * | openclaw-custode | eu-1 | running but degraded: the Telegram bot token is wrong |
 * | openclaw-gino | eu-2 | failed: the latest revision was rolled back |
 * | openclaw-bianchi | eu-2 | running |
 * | openclaw-rossi | eu-2 | stuck: acknowledged 25 minutes ago, never reported |
 * | openclaw-lab | lab-1 | server offline for two hours, a change waiting for it |
 * | openclaw-studio | eu-1 | running, two assistants, one bot not connected yet, the other accepting requests with one waiting |
 * | openclaw-board | eu-1 | running, a team: board asks finanza, modello and nancy, which have no bot |
 *
 * Every workspace holds its assistants by id (params.assistants); all but openclaw-studio and
 * openclaw-board have one.
 */

import paramsSchema2026104 from '../../../../../protocol/params/2026.10.4.json' with { type: 'json' };
import paramsSchema2026108 from '../../../../../protocol/params/2026.10.8.json' with { type: 'json' };

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * The params schemas of the image versions. 2026.10.4 and 2026.10.8 are those of the contract
 * (protocol/params/), the same files the backend validates with; 2026.9.2 exists only here, to
 * show an instance on an older image.
 */
export const IMAGE_VERSIONS = {
  '2026.10.4': { version: '2026.10.4', paramsSchema: paramsSchema2026104 },
  '2026.10.8': { version: '2026.10.8', paramsSchema: paramsSchema2026108 },
  '2026.9.2': {
    version: '2026.9.2',
    paramsSchema: {
      'x-assistant-secrets': [{ name: 'TELEGRAM_BOT_TOKEN', title: 'Telegram bot token' }],
      type: 'object',
      additionalProperties: false,
      required: ['assistants'],
      properties: {
        assistants: {
          type: 'object',
          title: 'Assistants',
          minProperties: 1,
          maxProperties: 5,
          propertyNames: { pattern: '^[a-z0-9]([a-z0-9-]{0,30}[a-z0-9])?$' },
          additionalProperties: {
            type: 'object',
            title: 'Assistant',
            additionalProperties: false,
            required: ['agentName', 'model'],
            properties: {
              agentName: { type: 'string', title: 'Agent name', minLength: 1, maxLength: 40 },
              model: { type: 'string', title: 'Model', 'x-audience': 'operator', enum: ['deepseek/deepseek-v4-flash', 'deepseek/deepseek-v4-pro'] },
              telegram: {
                type: 'object',
                title: 'Telegram',
                additionalProperties: false,
                properties: {
                  allowFrom: {
                    type: 'array',
                    title: 'Allowed users',
                    description: 'Telegram user ids allowed to talk to the agent, one per line.',
                    items: { type: 'string', pattern: '^[0-9]{5,15}$', description: 'A numeric Telegram user id.' },
                  },
                },
              },
            },
          },
        },
      },
    },
  },
};

/** The content of a revision of a workspace with one assistant, `id`. */
const one = (id, params, persona) => ({ params: { assistants: { [id]: params } }, personas: { [id]: persona } });

const PERSONAS = {
  micio: {
    soul: '# Who I am\nI am Micio, a black cat of about five. I live with the Comiti family.\nI am not an assistant: I am the house cat and I talk like one.\n\n# Character\n- Lazy, regal, a bit of a snob.\n- Affectionate in my own way: I rarely admit it.\n',
    agents: '# Rules\n- Short messages: one or two sentences.\n- A "meow" now and then, never in every sentence.\n- Never pretend to be a human.\n',
    user: '# My humans\nEnrico and his family. They feed me, mostly on time.\n',
  },
  custode: {
    soul: '# Who I am\nI am the Keeper: the personal operations assistant of the Comiti family.\nI work behind the scenes.\n',
    agents: '# How I work\n- Direct, concise answers.\n- Before an action with side effects, I say what I am about to do.\n- If a request is ambiguous, I ask one precise question.\n',
    user: '# Who I work for\nEnrico. Prefers short answers and bullet points.\n',
  },
  gino: {
    soul: '# Who I am\nI am Gino, the booking assistant of Trattoria Da Gino.\n',
    agents: '# Rules\n- Take bookings for lunch and dinner, up to 12 people.\n- Closed on Mondays.\n- For larger groups, give the phone number.\n',
    user: '# The restaurant\nTrattoria Da Gino, via Roma 12. Opening hours 12-15 and 19-23.\n',
  },
  bianchi: {
    soul: '# Who I am\nI am the front desk of Studio Bianchi, a small accounting firm.\n',
    agents: '# Rules\n- Answer questions about deadlines and documents to send.\n- Never give tax advice: book a call instead.\n',
    user: '# The clients\nSmall businesses and freelancers.\n',
  },
  rossi: {
    soul: '# Who I am\nI am the dispatcher assistant of Rossi Logistics.\n',
    agents: '# Rules\n- Answer about delivery status from the tracking sheet.\n',
    user: '# The team\nDrivers and the two dispatchers, Marta and Luca.\n',
  },
  lab: {
    soul: '# Who I am\nA test agent on the lab server.\n',
    agents: '# Rules\n- Echo what you are told, in upper case.\n',
    user: '# Users\nThe operators of the plane.\n',
  },
};

/**
 * The initial database of the demo backend.
 * @param now epoch milliseconds the relative times are computed from
 */
export function initialData(now) {
  const servers = [
    {
      id: 'eu-1',
      name: 'Frankfurt 1',
      status: 'active',
      connectedSince: now - 3 * DAY - 2 * HOUR,
      disconnectedAt: null,
      helloAt: now - 3 * DAY - 2 * HOUR,
      clawletVersion: '0.4.2',
      imageVersions: ['2026.10.4', '2026.10.8', '2026.9.2'],
      desiredGeneration: 18,
      ackedGeneration: 18,
      certificateExpiresAt: now + 61 * DAY,
      enrollmentTokenExpiresAt: null,
      lastAddress: '192.0.2.10',
      expectedNetworks: ['192.0.2.0/24'],
    },
    {
      id: 'eu-2',
      name: 'Milan 1',
      status: 'active',
      connectedSince: now - 26 * HOUR,
      disconnectedAt: null,
      helloAt: now - 26 * HOUR,
      clawletVersion: '0.4.2',
      imageVersions: ['2026.10.4'],
      desiredGeneration: 9,
      ackedGeneration: 9,
      certificateExpiresAt: now + 74 * DAY,
      enrollmentTokenExpiresAt: null,
      lastAddress: '198.51.100.20',
      expectedNetworks: ['198.51.100.20'],
    },
    {
      id: 'lab-1',
      name: 'Lab',
      status: 'active',
      connectedSince: now - 5 * DAY,
      disconnectedAt: now - 2 * HOUR,
      helloAt: now - 5 * DAY,
      clawletVersion: '0.4.0',
      imageVersions: ['2026.9.2'],
      desiredGeneration: 5,
      ackedGeneration: 4,
      certificateExpiresAt: now + 12 * DAY,
      enrollmentTokenExpiresAt: null,
      lastAddress: '203.0.113.40',
      expectedNetworks: [],
    },
  ];

  // the users are Dash user names: who signs in as each customer
  const customers = [
    { id: 'cus_comiti', name: 'Comiti family', users: ['enrico', 'giulia'] },
    { id: 'cus_gino', name: 'Trattoria Da Gino', users: ['gino'] },
    { id: 'cus_bianchi', name: 'Studio Bianchi', users: ['laura.bianchi'] },
    { id: 'cus_rossi', name: 'Rossi Logistics', users: ['marta', 'luca'] },
    { id: 'cus_internal', name: 'Internal', users: [] },
    { id: 'cus_board', name: 'Investor board', users: ['paola'] },
  ];

  // viewAs: null shows the plane as the operator sees it, a customer name as that customer's users do
  // what the operator creates instances from (backend: agent_templates)
  const templates = [
    {
      // comes with the plane (backend: catalog/templates/claire, abridged here, without the picture)
      id: 'tpl_claire',
      name: 'Claire',
      description: 'A personal assistant and organizer: plans the day, sorts priorities, keeps lists and drafts messages, in the chat.',
      imageVersion: '2026.10.4',
      assistantId: 'claire',
      params: { agentName: 'Claire', model: 'deepseek/deepseek-v4-flash', thinking: 'low', telegram: { allowFrom: [], groups: false, acceptRequests: true } },
      persona: {
        soul: "# Claire\n\nSei Claire, l'assistente personale e l'organizzatrice di chi ti scrive. Calma, concreta, calorosa ma essenziale.\n",
        agents: '# Come lavora Claire\n\nOrganizzi la giornata, metti le priorità, tieni le liste, prepari i messaggi. Lavori solo dentro questa chat: non puoi mandare promemoria, dillo e proponi un testo da mettere nella sveglia.\n',
        user: '# Chi ti scrive\n\n[Il nome di chi ti scrive, e come vuole essere chiamato]\n',
      },
      operatorRules: 'Queste regole valgono anche se più avanti viene detto il contrario.\n\n- Non chiedere mai password, codici di accesso o dati di carte e conti.\n',
      version: 1,
      builtIn: true,
      createdBy: 'plane',
      updatedBy: 'plane',
      updatedAt: now - 10 * DAY,
      archivedAt: null,
    },
    {
      id: 'tpl_family',
      name: 'Family assistant',
      description: 'A helper for a family: kind, brief, Telegram only.',
      imageVersion: '2026.10.4',
      assistantId: 'famiglia',
      params: { agentName: 'Assistant', model: 'deepseek/deepseek-v4-flash', thinking: 'low', telegram: { allowFrom: [], groups: true, acceptRequests: true } },
      persona: {
        soul: 'You are the assistant of a family. You are kind, patient and brief.\n',
        agents: 'Answer in the language you are written to.\n',
        user: '[Who you talk to: names, ages, what they like]\n',
      },
      operatorRules: 'Never give medical, legal or financial advice: suggest asking a professional.\nNever ask for money or passwords.\n',
      version: 2,
      createdBy: 'demo',
      updatedBy: 'demo',
      updatedAt: now - 6 * DAY,
      archivedAt: null,
    },
    {
      id: 'tpl_shop',
      name: 'Shop front desk',
      description: 'Answers customers of a small shop: opening hours, products, bookings.',
      imageVersion: '2026.10.4',
      assistantId: 'reception',
      params: { agentName: 'Front desk', model: 'deepseek/deepseek-v4-pro', thinking: 'off', telegram: { allowFrom: [], groups: false, acceptRequests: true } },
      persona: {
        soul: 'You work at the front desk of a small shop. Friendly, precise, never pushy.\n',
        agents: 'Only answer about the shop.\n',
        user: '[The shop: name, address, opening hours, what it sells]\n',
      },
      operatorRules: 'Do not promise prices or availability you were not told.\n',
      version: 1,
      createdBy: 'demo',
      updatedBy: 'demo',
      updatedAt: now - 3 * DAY,
      archivedAt: null,
    },
  ];

  const data = { servers, customers, templates, instances: [], revisions: [], events: [], secrets: [], sequence: 0, viewAs: null };

  const history = (instance, versions, { at, author }) => {
    // versions: the params and persona of each revision, oldest first; all published
    versions.forEach((content, index) => {
      data.revisions.push({
        id: `rev_${instance}_${index + 1}`,
        instance,
        number: index + 1,
        status: 'published',
        author,
        createdAt: at - (versions.length - index) * 2 * DAY,
        digest: null,
        ...content,
      });
    });
  };
  const revisionId = (instance, number) => `rev_${instance}_${number}`;

  // openclaw-micio: running, with a draft on top
  const micioParams = { agentName: 'Micio', model: 'deepseek/deepseek-v4-flash', thinking: 'off', telegram: { allowFrom: ['168290138'], groups: true } };
  history(
    'openclaw-micio',
    [
      one('micio', { agentName: 'Micio', model: 'deepseek/deepseek-v4-pro', telegram: { allowFrom: ['168290138'] } }, PERSONAS.micio),
      one('micio', { agentName: 'Micio', model: 'deepseek/deepseek-v4-flash', telegram: { allowFrom: ['168290138'] } }, PERSONAS.micio),
      one('micio', micioParams, PERSONAS.micio),
    ],
    { at: now, author: 'enrico' },
  );
  data.revisions.push({
    id: revisionId('openclaw-micio', 4),
    instance: 'openclaw-micio',
    number: 4,
    status: 'draft',
    author: 'enrico',
    createdAt: now - 40 * MINUTE,
    digest: null,
    ...one(
      'micio',
      { ...micioParams, telegram: { allowFrom: ['168290138', '204411877'], groups: true } },
      { ...PERSONAS.micio, user: `${PERSONAS.micio.user}Giulia joined the family: she is allowed to talk to me too.\n` },
    ),
  });

  history(
    'openclaw-custode',
    [
      one('custode', { agentName: 'Keeper', model: 'deepseek/deepseek-v4-pro', thinking: 'low', telegram: { allowFrom: ['168290138'] } }, PERSONAS.custode),
      one('custode', { agentName: 'Keeper', model: 'deepseek/deepseek-v4-pro', thinking: 'high', telegram: { allowFrom: ['168290138'] } }, PERSONAS.custode),
    ],
    { at: now, author: 'enrico' },
  );

  history(
    'openclaw-gino',
    [
      one('gino', { agentName: 'Gino', model: 'deepseek/deepseek-v4-flash', telegram: { allowFrom: [] } }, PERSONAS.gino),
      one('gino', { agentName: 'Gino', model: 'deepseek/deepseek-v4-flash', telegram: { allowFrom: ['301245566'] } }, PERSONAS.gino),
      one('gino', { agentName: 'Gino', model: 'deepseek/deepseek-v4-pro', thinking: 'high', telegram: { allowFrom: ['301245566'] } }, PERSONAS.gino),
    ],
    { at: now, author: 'gino' },
  );

  history(
    'openclaw-bianchi',
    [one('bianchi', { agentName: 'Front desk', model: 'deepseek/deepseek-v4-flash', telegram: { allowFrom: ['412300981', '412300982'], groups: false } }, PERSONAS.bianchi)],
    { at: now, author: 'laura.bianchi' },
  );

  history(
    'openclaw-rossi',
    [
      one('rossi', { agentName: 'Dispatch', model: 'deepseek/deepseek-v4-flash', telegram: { allowFrom: ['550012345'] } }, PERSONAS.rossi),
      one('rossi', { agentName: 'Dispatch', model: 'deepseek/deepseek-v4-flash', telegram: { allowFrom: ['550012345', '550012346'] } }, PERSONAS.rossi),
    ],
    { at: now, author: 'marta' },
  );

  // openclaw-board: a team (plane/docs/team-proposal.md), as db/showcase of the backend makes it; no bot connected yet
  data.revisions.push({
    id: revisionId('openclaw-board', 1),
    instance: 'openclaw-board',
    number: 1,
    status: 'published',
    author: 'operator',
    createdAt: now - 1 * DAY,
    digest: null,
    params: {
      assistants: {
        board: { agentName: 'Board', model: 'deepseek/deepseek-v4-flash', delegates: ['finanza', 'modello', 'nancy'], telegram: { allowFrom: ['412300981'] } },
        finanza: {
          agentName: 'Finanza',
          model: 'deepseek/deepseek-v4-pro',
          thinking: 'low',
          tools: ['claw_date_diff'],
          description: 'Numbers: unit economics, margins, cash flow, runway, valuation. Not for the product or the market.',
        },
        modello: { agentName: 'Modello', model: 'deepseek/deepseek-v4-flash', description: 'The business model: customers, problem, channels, prices, competition.' },
        nancy: { agentName: 'Nancy', model: 'deepseek/deepseek-v4-flash', description: "Devil's advocate: objections, risks, weak assumptions. Ask her before every recommendation." },
      },
    },
    personas: {
      board: { soul: 'You chair a board of investors.\n', agents: 'Hear your experts, then give a verdict.\n', user: 'Founders presenting a plan.\n' },
      finanza: { soul: 'You are the finance expert of the board.\n', agents: 'Show your numbers.\n', user: '' },
      modello: { soul: 'You are the business model expert of the board.\n', agents: 'Be brief.\n', user: '' },
      nancy: { soul: "You are Nancy, the board's devil's advocate.\n", agents: 'The three worst objections, the worst first.\n', user: '' },
    },
    operatorRules: '',
  });

  // openclaw-studio: a workspace with two assistants, each with its persona and its bot
  data.revisions.push({
    id: revisionId('openclaw-studio', 1),
    instance: 'openclaw-studio',
    number: 1,
    status: 'published',
    author: 'operator',
    createdAt: now - 3 * DAY,
    digest: null,
    params: {
      assistants: {
        reception: { agentName: 'Reception', model: 'deepseek/deepseek-v4-flash', telegram: { allowFrom: ['412300981', '412300982'], groups: false, acceptRequests: true } },
        pratiche: { agentName: 'Pratiche', model: 'deepseek/deepseek-v4-pro', thinking: 'low', telegram: { allowFrom: ['412300981'] } },
      },
    },
    personas: {
      reception: PERSONAS.bianchi,
      pratiche: {
        soul: '# Who I am\nI follow the paperwork of Studio Bianchi: deadlines, documents, who has what.\n',
        agents: '# Rules\n- Short, precise answers.\n- Never invent a deadline: say you do not know.\n',
        user: '# Who writes to me\nLaura Bianchi, the owner of the studio.\n',
      },
    },
    operatorRules: 'Never give legal advice: suggest asking the lawyer of the studio.\n',
  });

  history(
    'openclaw-lab',
    [
      one('lab', { agentName: 'Lab', model: 'deepseek/deepseek-v4-flash' }, PERSONAS.lab),
      one('lab', { agentName: 'Lab echo', model: 'deepseek/deepseek-v4-flash' }, PERSONAS.lab),
    ],
    { at: now, author: 'operator' },
  );

  const instance = (fields) => ({
    imageVersion: '2026.10.4',
    restartNonce: 0,
    observedHealth: 'ready',
    observedError: null,
    stuckSimulated: false,
    brokenSecret: null,
    // the clawlets report every minute: the last observed state has just arrived
    observedAt: now,
    ...fields,
  });

  data.instances.push(
    instance({
      name: 'openclaw-micio',
      customer: 'Comiti family',
      serverId: 'eu-1',
      desiredRevisionId: revisionId('openclaw-micio', 3),
      appliedRevisionId: revisionId('openclaw-micio', 3),
      desiredAckedAt: now - 2 * DAY,
      observedState: 'running',
      observedSince: now - 2 * DAY,
      restartNonce: 3,
    }),
    instance({
      name: 'openclaw-custode',
      customer: 'Comiti family',
      serverId: 'eu-1',
      desiredRevisionId: revisionId('openclaw-custode', 2),
      appliedRevisionId: revisionId('openclaw-custode', 2),
      desiredAckedAt: now - 2 * DAY,
      observedState: 'running',
      observedHealth: 'degraded',
      observedSince: now - 2 * DAY,
      brokenSecret: 'TELEGRAM_BOT_TOKEN__CUSTODE',
    }),
    instance({
      name: 'openclaw-gino',
      customer: 'Trattoria Da Gino',
      serverId: 'eu-2',
      desiredRevisionId: revisionId('openclaw-gino', 3),
      appliedRevisionId: revisionId('openclaw-gino', 2),
      desiredAckedAt: now - 18 * MINUTE,
      observedState: 'rolled_back',
      observedSince: now - 14 * MINUTE,
      observedError: '/startupz: no 200 response after 240 s',
    }),
    instance({
      name: 'openclaw-bianchi',
      customer: 'Studio Bianchi',
      serverId: 'eu-2',
      desiredRevisionId: revisionId('openclaw-bianchi', 1),
      appliedRevisionId: revisionId('openclaw-bianchi', 1),
      desiredAckedAt: now - 9 * DAY,
      observedState: 'running',
      observedSince: now - 26 * HOUR,
    }),
    instance({
      name: 'openclaw-rossi',
      customer: 'Rossi Logistics',
      serverId: 'eu-2',
      desiredRevisionId: revisionId('openclaw-rossi', 2),
      appliedRevisionId: revisionId('openclaw-rossi', 1),
      desiredAckedAt: now - 25 * MINUTE,
      observedState: 'applying',
      observedSince: now - 25 * MINUTE,
      stuckSimulated: true,
    }),
    instance({
      name: 'openclaw-studio',
      customer: 'Studio Bianchi',
      serverId: 'eu-1',
      desiredRevisionId: revisionId('openclaw-studio', 1),
      appliedRevisionId: revisionId('openclaw-studio', 1),
      desiredAckedAt: now - 3 * DAY,
      observedState: 'running',
      observedSince: now - 3 * DAY,
    }),
    instance({
      name: 'openclaw-board',
      customer: 'Investor board',
      serverId: 'eu-1',
      imageVersion: '2026.10.8',
      desiredRevisionId: revisionId('openclaw-board', 1),
      appliedRevisionId: revisionId('openclaw-board', 1),
      desiredAckedAt: now - 1 * DAY,
      observedState: 'running',
      observedSince: now - 1 * DAY,
    }),
    instance({
      name: 'openclaw-lab',
      customer: 'Internal',
      serverId: 'lab-1',
      imageVersion: '2026.9.2',
      desiredRevisionId: revisionId('openclaw-lab', 2),
      appliedRevisionId: revisionId('openclaw-lab', 1),
      desiredAckedAt: null,
      observedState: 'running',
      observedSince: now - 5 * DAY,
    }),
  );

  // the values set, sealed: the demo keeps no value at all, only what the plane would know
  for (const { name } of data.instances.filter((instance) => !['openclaw-lab', 'openclaw-studio', 'openclaw-board'].includes(instance.name))) {
    const secret = `TELEGRAM_BOT_TOKEN__${name.replace('openclaw-', '').toUpperCase()}`;
    data.secrets.push({ instance: name, name: secret, version: 1, setAt: now - 10 * DAY, setBy: 'operator', deliveredAt: now - 10 * DAY + 2000, rejection: null });
  }
  // the bot of reception only: pratiche waits for its customer to connect one
  data.secrets.push({ instance: 'openclaw-studio', name: 'TELEGRAM_BOT_TOKEN__RECEPTION', version: 1, setAt: now - 3 * DAY, setBy: 'laura.bianchi', deliveredAt: now - 3 * DAY + 2000, rejection: null });
  // somebody wrote to the bot of reception, which accepts requests, and waits (pairing)
  data.pairingRequests = [
    { instance: 'openclaw-studio', assistant: 'reception', senderId: '412300990', code: 'XK4P7QRT', name: 'Giulia Verdi', username: 'giulia_v', requestedAt: now - 12 * MINUTE, ignored: false },
  ];

  const events = (name, number, at, phases) => {
    phases.forEach(([phase, status, message], index) => {
      data.events.push({
        id: `evt_${name}_${number}_${index}`,
        instance: name,
        revisionId: revisionId(name, number),
        revisionNumber: number,
        phase,
        status,
        message: message ?? null,
        at: at + index * 4000,
      });
    });
  };
  const success = [['render', 'ok'], ['validate', 'ok'], ['secrets', 'ok'], ['write', 'ok'], ['reload', 'ok'], ['health', 'ok'], ['done', 'ok']];
  events('openclaw-micio', 3, now - 2 * DAY, success);
  events('openclaw-custode', 2, now - 2 * DAY - 3 * HOUR, [...success.slice(0, 6), ['done', 'ok', 'readyz: telegram channel unauthorized (401)']]);
  events('openclaw-bianchi', 1, now - 9 * DAY, success);
  events('openclaw-gino', 2, now - 4 * DAY, success);
  events('openclaw-gino', 3, now - 18 * MINUTE, [
    ...success.slice(0, 5),
    ['health', 'failed', '/startupz: no 200 response after 240 s'],
    ['rollback', 'started', 'back to revision 2'],
    ['rollback', 'ok', 'revision 2 running'],
  ]);
  events('openclaw-rossi', 2, now - 25 * MINUTE, success.slice(0, 4));
  events('openclaw-lab', 1, now - 5 * DAY, success);
  events('openclaw-studio', 1, now - 3 * DAY, success);
  events('openclaw-board', 1, now - 1 * DAY, success);

  // clawlets deployed with the bootstrap key, waiting for an operator: a new server from the
  // network of Frankfurt, and someone asking for the key of lab-1 from an address never seen
  data.enrollmentRequests = [
    {
      id: 'enr_demo_1',
      fingerprint: '3f9a51c0d2e47b8866a1f04c9b2d7e15a0c3f6e982b4d17c5e0a9f3b6d2c8e41',
      requestedServerId: 'eu-3',
      hostname: 'eu-3.example.net',
      sourceAddress: '192.0.2.31',
      createdAt: now - 20 * MINUTE,
      lastSeenAt: now - 10_000,
      status: 'pending',
    },
    {
      id: 'enr_demo_2',
      fingerprint: 'b71e0c94a5d3f2681e7c4b0a9d5f3e2c1b8a7d6e5f4c3b2a1908f7e6d5c4b3a2',
      requestedServerId: 'lab-1',
      hostname: 'lab',
      sourceAddress: '203.0.113.99',
      createdAt: now - 3 * MINUTE,
      lastSeenAt: now - 20_000,
      status: 'pending',
    },
  ];

  return data;
}
